import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Layers, Cpu, Box, Wrench, DollarSign, Loader2, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { BACKEND_URL } from "../utils/backendUrl";
const API = `${BACKEND_URL}/api`;

const CalculatorPage = () => {
  const [materials, setMaterials] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [weightGrams, setWeightGrams] = useState(50);
  const [infill, setInfill] = useState(20);
  const [printTime, setPrintTime] = useState(2);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // Comparator mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [printer2, setPrinter2] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matRes, printRes] = await Promise.all([
          axios.get(`${API}/materials`),
          axios.get(`${API}/printers`)
        ]);
        // Handle paginated response
        setMaterials(matRes.data.materials || matRes.data);
        setPrinters(printRes.data.printers || []);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.debug('CalculatorPage fetch failed:', error.message);
      }
    };
    fetchData();
  }, []);

  const selectedPrinterData = (selectedPrinter && selectedPrinter !== "none") ? printers.find(p => p.id === selectedPrinter) : null;
  const printer2Data = (printer2 && printer2 !== "none") ? printers.find(p => p.id === printer2) : null;

  const calculate = async () => {
    if (!selectedMaterial) { toast.warning("Selecciona un material"); return; }
    setLoading(true);
    try {
      if (compareMode) {
        if (!selectedPrinter || selectedPrinter === "none" || !printer2 || printer2 === "none") {
          toast.warning("Selecciona dos impresoras para comparar");
          setLoading(false); return;
        }
        const response = await axios.post(`${API}/compare-cost`, {
          material_id: selectedMaterial,
          weight_grams: weightGrams,
          infill_percentage: infill,
          printer_ids: [selectedPrinter, printer2]
        });
        setCompareResult(response.data);
        setResult(null);
      } else {
        const response = await axios.post(`${API}/calculate-cost`, {
          material_id: selectedMaterial,
          weight_grams: weightGrams,
          infill_percentage: infill,
          print_time_hours: printTime,
          printer_id: (selectedPrinter && selectedPrinter !== "none") ? selectedPrinter : null
        });
        setResult(response.data);
        setCompareResult(null);
      }
    } catch (error) {
      toast.error("Error al calcular");
    } finally {
      setLoading(false);
    }
  };

  // Group printers by brand
  const printersByBrand = printers.reduce((acc, p) => {
    if (!acc[p.brand]) acc[p.brand] = [];
    acc[p.brand].push(p);
    return acc;
  }, {});

  const PrinterSelect = ({ value, onChange, testId, label }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder="Selecciona una impresora..." />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {!compareMode && <SelectItem value="none">Sin impresora (manual)</SelectItem>}
          {Object.entries(printersByBrand).map(([brand, items]) => (
            <React.Fragment key={brand}>
              <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">{brand}</div>
              {items.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name} — €{p.price_eur.toLocaleString('es-ES')}</SelectItem>
              ))}
            </React.Fragment>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const PrinterSpecs = ({ printerData }) => printerData ? (
    <div className="bg-muted/50 rounded p-3 text-xs space-y-1 border border-border">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Tecnología</span>
        <span className="font-medium">{printerData.technology}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Volumen</span>
        <span className="font-mono">{printerData.build_volume_mm.x}×{printerData.build_volume_mm.y}×{printerData.build_volume_mm.z} mm</span>
      </div>
      {printerData.typical_speed_mm_s && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Velocidad</span>
          <span className="font-mono">{printerData.typical_speed_mm_s} mm/s</span>
        </div>
      )}
      <div className="flex justify-between">
        <span className="text-muted-foreground">Consumo</span>
        <span className="font-mono">{printerData.power_w}W</span>
      </div>
    </div>
  ) : null;

  // ── Comparison table ──────────────────────────────────────────
  const CompareTable = ({ data }) => {
    const { results, winners } = data;
    const isWinner = (pid, cat) => winners[cat]?.includes(pid);
    const WinBadge = () => <span className="ml-1 text-[10px] bg-[#1a8ccc] text-white px-1 rounded">mejor</span>;

    const rows = [
      { label: "Tiempo estimado", key: "estimated_time_label", numKey: "estimated_time_hours", mono: true },
      { label: "Material", key: "material_cost", numKey: "material_cost", prefix: "€", mono: true },
      { label: "Electricidad", key: "electricity_cost", numKey: "electricity_cost", prefix: "€", mono: true },
      { label: "Depreciación", key: "depreciation_cost", numKey: "depreciation_cost", prefix: "€", mono: true },
    ];

    return (
      <Card className="border-border" data-testid="compare-result">
        <CardHeader>
          <CardTitle>Comparativa</CardTitle>
          <CardDescription>{data.material} · {weightGrams}g · {infill}% relleno</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Header row */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div />
            {results.map(r => (
              <div key={r.printer_id} className="text-center">
                <p className="font-semibold text-xs leading-tight">{r.printer_name}</p>
                <p className="text-[10px] text-muted-foreground">€{r.printer_price_eur.toLocaleString('es-ES')}</p>
              </div>
            ))}
          </div>

          {/* Data rows */}
          <div className="space-y-1">
            {rows.map(row => (
              <div key={row.numKey} className="grid grid-cols-3 gap-2 py-2 border-b border-border">
                <span className="text-xs text-muted-foreground self-center">{row.label}</span>
                {results.map(r => {
                  const win = isWinner(r.printer_id, row.numKey);
                  const val = row.prefix ? `€${r[row.key]}` : r[row.key];
                  return (
                    <div key={r.printer_id} className={`text-center font-mono text-sm ${win ? 'text-[#1a8ccc] font-bold' : ''}`}>
                      {val}{win && <WinBadge />}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Total row */}
          <div className="grid grid-cols-3 gap-2 pt-4 border-t-2 border-foreground mt-4">
            <span className="font-bold self-center">TOTAL</span>
            {results.map(r => {
              const win = isWinner(r.printer_id, "total_cost");
              return (
                <div key={r.printer_id} className={`text-center font-mono text-xl font-bold ${win ? 'text-[#1a8ccc]' : 'text-muted-foreground'}`} data-testid={`compare-total-${r.printer_id}`}>
                  €{r.total_cost}
                  {win && <div className="text-[10px] font-normal text-[#1a8ccc]">más económica</div>}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            * Precio material igual para ambas. Diferencias por velocidad, consumo y depreciación del equipo.
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="calculator-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl mb-2">CALCULADORA DE COSTOS</h1>
            <p className="text-muted-foreground">Estima el costo y tiempo de tu impresión 3D</p>
          </div>
          <Button
            variant={compareMode ? "default" : "outline"}
            onClick={() => { setCompareMode(!compareMode); setResult(null); setCompareResult(null); }}
            className={compareMode ? "bg-[#1a8ccc] hover:bg-[#1575b0] text-white" : ""}
            data-testid="compare-mode-toggle"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            {compareMode ? "Modo comparación activo" : "Comparar impresoras"}
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Parameters */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle>{compareMode ? "Comparar impresoras" : "Parámetros de Impresión"}</CardTitle>
              {compareMode && <CardDescription>Misma pieza, dos impresoras diferentes</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Printer selectors */}
              {compareMode ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <PrinterSelect value={selectedPrinter} onChange={setSelectedPrinter} testId="calc-printer-1" label="Impresora 1" />
                    <PrinterSpecs printerData={selectedPrinterData} />
                  </div>
                  <div className="space-y-2">
                    <PrinterSelect value={printer2} onChange={setPrinter2} testId="calc-printer-2" label="Impresora 2" />
                    <PrinterSpecs printerData={printer2Data} />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <PrinterSelect value={selectedPrinter} onChange={setSelectedPrinter} testId="calc-printer-select" label="Impresora (opcional)" />
                  <PrinterSpecs printerData={selectedPrinterData} />
                </div>
              )}

              {/* Material */}
              <div className="space-y-2">
                <Label>Material</Label>
                <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
                  <SelectTrigger data-testid="calc-material-select">
                    <SelectValue placeholder="Selecciona un material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map((mat) => (
                      <SelectItem key={mat.id} value={mat.id}>
                        {mat.name} — €{mat.price_per_kg}/kg
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Weight */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Peso de la pieza (gramos)</Label>
                  <span className="font-mono">{weightGrams}g</span>
                </div>
                <Slider value={[weightGrams]} onValueChange={([v]) => setWeightGrams(v)} min={1} max={500} step={1} data-testid="calc-weight-slider" />
              </div>

              {/* Infill */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Relleno (%)</Label>
                  <span className="font-mono">{infill}%</span>
                </div>
                <Slider value={[infill]} onValueChange={([v]) => setInfill(v)} min={5} max={100} step={5} data-testid="calc-infill-slider" />
              </div>

              {/* Manual time (single mode, no printer) */}
              {!compareMode && (!selectedPrinter || selectedPrinter === "none") && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Tiempo de impresión estimado (horas)</Label>
                    <span className="font-mono">{printTime}h</span>
                  </div>
                  <Slider value={[printTime]} onValueChange={([v]) => setPrintTime(v)} min={0.5} max={48} step={0.5} data-testid="calc-time-slider" />
                  <p className="text-xs text-muted-foreground">Selecciona una impresora para calcular automáticamente</p>
                </div>
              )}

              <Button
                onClick={calculate}
                disabled={loading}
                className="w-full rounded-sm bg-[#1a8ccc] hover:bg-[#1575b0] text-white"
                data-testid="calc-submit-btn"
              >
                {loading ? "CALCULANDO..." : compareMode ? "COMPARAR IMPRESORAS" : "CALCULAR COSTO"}
              </Button>
            </CardContent>
          </Card>

          {/* Right: Results */}
          <div>
            {compareResult ? (
              <CompareTable data={compareResult} />
            ) : result ? (
              <Card className="border-border" data-testid="calc-result">
                <CardHeader>
                  <CardTitle>Resultado</CardTitle>
                  <CardDescription>{result.material}{result.printer && ` · ${result.printer}`}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className={`flex justify-between py-2 border-b border-border ${result.printer ? 'text-[#1a8ccc]' : ''}`}>
                      <span className={result.printer ? 'font-medium' : 'text-muted-foreground'}>
                        {result.printer ? 'Tiempo estimado' : 'Tiempo (manual)'}
                      </span>
                      <span className="font-mono font-semibold" data-testid="calc-time-result">{result.estimated_time_label}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Peso efectivo</span>
                      <span className="font-mono">{result.effective_weight_grams?.toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Costo material</span>
                      <span className="font-mono">€{result.material_cost}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Electricidad {result.printer_power_w && <span className="text-xs">({result.printer_power_w}W)</span>}</span>
                      <span className="font-mono">€{result.electricity_cost}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Depreciación {result.printer && <span className="text-xs">(amort. 5000h)</span>}</span>
                      <span className="font-mono">€{result.depreciation_cost}</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t-2 border-foreground">
                    <div className="flex justify-between items-center">
                      <span className="text-xl">COSTO TOTAL</span>
                      <span className="font-mono text-3xl text-[#1a8ccc]" data-testid="calc-total-cost">€{result.total_cost}</span>
                    </div>
                  </div>
                  {result.printer && result.printer_speed_mm_s && (
                    <p className="text-xs text-muted-foreground pt-2">* Tiempo calculado a {result.printer_speed_mm_s} mm/s · capa 0,2mm · eficiencia 30%</p>
                  )}
                  {result.printer && !result.printer_speed_mm_s && (
                    <p className="text-xs text-muted-foreground pt-2">* Tiempo estimado por tecnología SLA (capas de 0,1mm)</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="h-full flex items-center justify-center border border-dashed border-border p-12 rounded-lg">
                <div className="text-center space-y-3">
                  <Calculator className="w-12 h-12 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {compareMode
                      ? "Selecciona dos impresoras y un material para comparar"
                      : "Configura los parámetros y calcula el costo"}
                  </p>
                  {!compareMode && <p className="text-xs text-muted-foreground">Activa el modo comparación para ver dos impresoras lado a lado</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default CalculatorPage;
